import { getQueryString } from '#utils/parseUrl';
import api, { getSource } from '#config/api';

export const API_SOURCE_TOKENS = {
  getLegalCase: getSource()
};

// eslint-disable-next-line complexity
const unwrap = (res, fallbackMessage) => {
  if (res.ok) {
    return res.data;
  }
  const raw = Array.isArray(res.data) ? res.data[0] : res.data;
  const msg =
    (raw && typeof raw.message === 'string' && raw.message) ||
    (raw && typeof raw.error === 'string' && raw.error) ||
    res.problem;
  throw new Error(typeof msg === 'string' && msg ? msg : fallbackMessage);
};

export const getLegalCases = data => {
  API_SOURCE_TOKENS.getLegalCase.cancel();
  API_SOURCE_TOKENS.getLegalCase = getSource();
  return api.get(
    `/legalCases${getQueryString(data)}`,
    {},
    { cancelToken: API_SOURCE_TOKENS.getLegalCase.token }
  );
};

export const getLegalCasesV2 = async params => {
  const res = await api.get('/v2/legal-cases', params);
  return unwrap(res, 'Error al cargar casos legales');
};

export const getLegalCaseTags = async () => {
  const res = await api.get('/legalCases/tags');
  return unwrap(res, 'Error al cargar las etiquetas');
};

export const importLegalCases = data => api.post('/legalCases', data);
export const importLegalCase = ({ id, ...data }) => api.put(`/legalCases/${id}/import`, data);
export const getLegalCaseById = id => api.get(`/legalCases/${id}`);
export const getLegalCaseDetail = async id => {
  const res = await getLegalCaseById(id);
  return unwrap(res, 'Error al cargar el expediente');
};
export const getLegalCaseImportStatus = async id => {
  const res = await api.get(`/legalCases/${id}/import-status`);
  return unwrap(res, 'Error al consultar el estado de importación');
};
export const getEjeCaseParticipants = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/eje-participants`, params);
  return unwrap(res, 'Error al cargar los participantes');
};
export const getEjeCaseEntries = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/eje-entries`, params);
  return unwrap(res, 'Error al cargar las actuaciones');
};
export const getEjeRelatedCases = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/eje-related-cases`, params);
  return unwrap(res, 'Error al cargar las causas relacionadas');
};
export const getMevCaseSteps = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/mev-steps`, params);
  return unwrap(res, 'Error al cargar los pasos procesales');
};
export const getLegalCaseListField = ({ id, ...data }) =>
  api.get(`legalCases/${id}/listField${getQueryString(data)}`);
export const updateLegalCaseListField = ({ legalCaseId, id, ...data }) =>
  api.put(`legalCases/${legalCaseId}/listField/${id}`, data);
export const updateLegalCase = ({ id, ...data }) => api.put(`/legalCases/${id}`, data);
export const uploadEvidence = ({ id, evidence }) => api.post(`/legalCases/${id}/evidences`, evidence);
export const deleteEvidence = ({ legalCaseId, evidenceId }) =>
  api.delete(`/legalCases/${legalCaseId}/evidences/${evidenceId}`);
export const updateEvidence = ({ id, evidenceId, evidence }) =>
  api.put(`/legalCases/${id}/evidences/${evidenceId}`, evidence);
export const updateAction = ({ id, ...data }) => api.put(`/actions/${id}`, data);
export const searchPublicFile = data => api.post('/legalCases/public/search', data);
export const addPublicFile = (id, data = {}) => api.post(`/legalCases/public/add/${id}`, data);
export const leaveNote = id => api.put(`/legalCases/${id}/leaveNote`);
export const deleteLegalCase = id => api.delete(`/legalCases/${id}`);
export const previewLegalCase = async data => {
  const res = await api.post('/legal-cases/scraper/preview', data);
  return unwrap(res, 'Error al buscar el expediente');
};
export const importScraperLegalCase = async data => {
  const res = await api.post('/legal-cases/scraper/import', data);
  return unwrap(res, 'Error al importar el expediente');
};
export const createCustomLegalCase = async data => {
  const res = await api.post('/legalCases/custom', data);
  return unwrap(res, 'Error al crear el expediente');
};
export const syncLegalCase = async id => {
  const res = await api.post(`/legalCases/${id}/sync`);
  return unwrap(res, 'Error al sincronizar el expediente');
};

// Partes intervinientes
export const getCustomParticipants = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/custom-participants`, params);
  return unwrap(res, 'Error al cargar las partes intervinientes');
};
export const createCustomParticipant = async ({ id, ...data }) => {
  const res = await api.post(`/legalCases/${id}/custom-participants`, data);
  return unwrap(res, 'Error al crear la parte interviniente');
};
export const updateCustomParticipant = async ({ id, entityId, ...data }) => {
  const res = await api.put(`/legalCases/${id}/custom-participants/${entityId}`, data);
  return unwrap(res, 'Error al actualizar la parte interviniente');
};
export const deleteCustomParticipant = async ({ id, entityId }) => {
  const res = await api.delete(`/legalCases/${id}/custom-participants/${entityId}`);
  return unwrap(res, 'Error al eliminar la parte interviniente');
};

// Peritos intervinientes
export const getCustomExperts = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/custom-experts`, params);
  return unwrap(res, 'Error al cargar los peritos intervinientes');
};
export const createCustomExpert = async ({ id, ...data }) => {
  const res = await api.post(`/legalCases/${id}/custom-experts`, data);
  return unwrap(res, 'Error al crear el perito interviniente');
};
export const updateCustomExpert = async ({ id, entityId, ...data }) => {
  const res = await api.put(`/legalCases/${id}/custom-experts/${entityId}`, data);
  return unwrap(res, 'Error al actualizar el perito interviniente');
};
export const deleteCustomExpert = async ({ id, entityId }) => {
  const res = await api.delete(`/legalCases/${id}/custom-experts/${entityId}`);
  return unwrap(res, 'Error al eliminar el perito interviniente');
};

// Fiscales intervinientes
export const getCustomProsecutors = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/custom-prosecutors`, params);
  return unwrap(res, 'Error al cargar los fiscales intervinientes');
};
export const createCustomProsecutor = async ({ id, ...data }) => {
  const res = await api.post(`/legalCases/${id}/custom-prosecutors`, data);
  return unwrap(res, 'Error al crear el fiscal interviniente');
};
export const updateCustomProsecutor = async ({ id, entityId, ...data }) => {
  const res = await api.put(`/legalCases/${id}/custom-prosecutors/${entityId}`, data);
  return unwrap(res, 'Error al actualizar el fiscal interviniente');
};
export const deleteCustomProsecutor = async ({ id, entityId }) => {
  const res = await api.delete(`/legalCases/${id}/custom-prosecutors/${entityId}`);
  return unwrap(res, 'Error al eliminar el fiscal interviniente');
};

// Actuaciones
export const getCustomActions = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/custom-actions`, params);
  return unwrap(res, 'Error al cargar las actuaciones');
};
export const createCustomAction = async ({ id, ...data }) => {
  const res = await api.post(`/legalCases/${id}/custom-actions`, data);
  return unwrap(res, 'Error al crear la actuación');
};
export const updateCustomAction = async ({ id, entityId, ...data }) => {
  const res = await api.put(`/legalCases/${id}/custom-actions/${entityId}`, data);
  return unwrap(res, 'Error al actualizar la actuación');
};
export const deleteCustomAction = async ({ id, entityId }) => {
  const res = await api.delete(`/legalCases/${id}/custom-actions/${entityId}`);
  return unwrap(res, 'Error al eliminar la actuación');
};
export const getPjnActions = async ({ id, ...params }) => {
  const res = await api.get(`/legalCases/${id}/pjn-actions`, params);
  return unwrap(res, 'Error al cargar las actuaciones');
};

// Comentarios de actuaciones (genérico: actuaciones manuales, pasos MEV, entradas EJE)
export const getActionComments = async ({ id, entityId }) => {
  const res = await api.get(`/legalCases/${id}/actions/${entityId}/comments`);
  return unwrap(res, 'Error al cargar los comentarios');
};
export const createActionComment = async ({ id, entityId, ...data }) => {
  const res = await api.post(`/legalCases/${id}/actions/${entityId}/comments`, data);
  return unwrap(res, 'Error al crear el comentario');
};
export const updateActionComment = async ({ id, entityId, commentId, ...data }) => {
  const res = await api.put(`/legalCases/${id}/actions/${entityId}/comments/${commentId}`, data);
  return unwrap(res, 'Error al editar el comentario');
};
export const deleteActionComment = async ({ id, entityId, commentId }) => {
  const res = await api.delete(`/legalCases/${id}/actions/${entityId}/comments/${commentId}`);
  return unwrap(res, 'Error al eliminar el comentario');
};
